import { groupBy } from "lodash-es";
import React from "react";
import { GridList, ListLayout, Virtualizer } from "react-aria-components";

import { StatusIcon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import type { TAccession, TAccessionStatus } from "@/lib/types";
import { createArrayWithNumbers } from "@/lib/utils";
import { AccessionBoardItem, LoadingItem } from "./AccessionBoardItem";

const AccessionColumn = React.memo(
  ({
    accessions,
    status,
    loading,
    loadingItems = 3,
  }: {
    accessions: Record<string, TAccession[]>;
    status: TAccessionStatus;
    loading?: boolean;
    loadingItems?: number;
  }) => {
    const layout = React.useMemo(
      () =>
        new ListLayout({
          rowHeight: 100 + 10,
        }),
      [],
    );
    const total = accessions[status]?.length || 0;
    return (
      <div className="flex flex-col shrink-0 overflow-hidden p-2 w-[300px] max-w-[300px] snap-center bg-accent rounded-t-lg">
        <div className="flex items-center gap-2 justify-between w-full">
          <div className="flex items-center gap-1 p-2">
            <StatusIcon status={status} />
            <Text size="2" className="capitalize">
              {status.replace("_", " ") || "-"}
            </Text>
          </div>
          <span className="text-xs text-muted-foreground font-semibold tabular-nums pr-1">
            {total}
          </span>
        </div>
        {loading ? (
          <div className="p-2 flex-1 overflow-y-auto [&>*+*]:mt-2">
            {createArrayWithNumbers(loadingItems).map((idx) => (
              <LoadingItem key={idx} />
            ))}
          </div>
        ) : accessions[status] ? (
          <Virtualizer layout={layout}>
            <GridList
              className="flex-1 overflow-y-auto [&>*+*]:mt-2"
              items={accessions[status]}
              aria-label={`Accessions in ${status} status`}
            >
              {(accession) => {
                return (
                  <AccessionBoardItem
                    key={accession.id}
                    accession={accession}
                  />
                );
              }}
            </GridList>
          </Virtualizer>
        ) : null}
      </div>
    );
  },
);

export function AccessionBoard({
  accessions,
  loading,
}: {
  accessions: TAccession[];
  loading: boolean;
}) {
  const sorted = React.useMemo(
    () => groupBy(accessions, "status"),
    [accessions],
  );

  return (
    <div className="flex gap-3 px-8 pt-5 flex-1 overflow-y-hidden overflow-x-scroll bg-background snap-x snap-mandatory sm:snap-none">
      <AccessionColumn
        accessions={sorted}
        status="new"
        loading={loading}
        loadingItems={3}
      />
      <AccessionColumn
        accessions={sorted}
        status="received"
        loading={loading}
        loadingItems={2}
      />
      <AccessionColumn
        accessions={sorted}
        status="in_progress"
        loading={loading}
        loadingItems={8}
      />
      <AccessionColumn
        accessions={sorted}
        status="completed"
        loading={loading}
        loadingItems={1}
      />
    </div>
  );
}
